/**
 * The self-pay money rows under the Payment row (`patient-profile.mdx` → The money).
 *
 * The persistent answer to "what do they owe", as opposed to the order card's. The card lives at
 * its creation point on the timeline and sinks as events append above it, so the question asked
 * before every call is answered here instead. Both read the same `usePaymentOrder` instance: one
 * computation, two renderings, never two balances for one patient.
 */

import dayjs from 'dayjs';
import { collectsPayment, Track } from '@bariendo/lib/constants';
import { LedgerEntryType, TreatmentPaymentLedger, TreatmentPaymentOrder } from '@/components/Timeline/types';
import { formatDollars } from '@/services/money';
import { formatProcessor, sumRefunded } from '@/services/payments';
import { RefundHistoryEntry } from '@/services/Timeline/refundCards';
import { formatPanelDate } from './rows';

export interface BillingRow {
  label: string;
  value: string;
}

interface MoneyContext {
  /** Resolved by the caller: no recorded track is self-pay. */
  track: Track;
  /** A cancelling request exists. There is nothing left to owe, so Balance goes. */
  cancelled: boolean;
  now: Date;
}

function totalDiscount(order: TreatmentPaymentOrder): number {
  const lineDiscounts = order.items.reduce((sum, item) => sum + item.amountDiscount, 0);

  return (order.discount?.amount ?? 0) + lineDiscounts;
}

function netPrice(order: TreatmentPaymentOrder): string {
  const discount = totalDiscount(order);
  const net = formatDollars(order.amountTotal);

  return discount > 0 ? `${net} (${formatDollars(discount)} discount)` : net;
}

/**
 * One row per charge that took money, oldest first. **The method is the label**: a full date does
 * not fit the 75px label column, and the method is the one fact a refund needs.
 */
function paymentRows(ledger: TreatmentPaymentLedger): BillingRow[] {
  return ledger.entries
    .filter((entry) => entry.type === LedgerEntryType.PAYMENT && entry.amount > 0)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .map((entry) => ({
      label: formatProcessor(entry.processor) ?? entry.processor,
      value: `${formatPanelDate(entry.createdAt)} · ${formatDollars(entry.amount)}`,
    }));
}

/**
 * Refund due while a request has not left, Finance's approved amount once there is one; a dated
 * Refund row once the refund that left is linked. With no request behind it (a refund from before
 * requests existed), the ledger's refunded total stands alone, undated: `sumRefunded` is the only
 * place that arithmetic may live.
 */
function refundRows(ledger: TreatmentPaymentLedger, refunds: RefundHistoryEntry[]): BillingRow[] {
  const rows: BillingRow[] = [];
  const due = refunds
    .filter((request) => !request.linkedRefund)
    .reduce((sum, request) => sum + (request.amountApproved ?? request.amountRequested), 0);

  if (due > 0) rows.push({ label: 'Refund due', value: `−${formatDollars(due)}` });

  const issued = refunds.flatMap((request) => (request.linkedRefund ? [request.linkedRefund] : []));
  if (issued.length > 0) {
    for (const refund of [...issued].sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))) {
      rows.push({
        label: 'Refund',
        value: `${formatPanelDate(refund.occurredAt)} · −${formatDollars(refund.amount)}`,
      });
    }
    return rows;
  }

  const refunded = sumRefunded(ledger.entries);
  if (refunded > 0) rows.push({ label: 'Refund', value: `−${formatDollars(refunded)}` });

  return rows;
}

/**
 * Rows in render order. Empty with no order, which is most patients, and on a track that never
 * charges the patient: the Payment row carries their payor instead.
 */
export function moneyRows(
  order: TreatmentPaymentOrder | null,
  ledger: TreatmentPaymentLedger,
  refunds: RefundHistoryEntry[],
  { track, cancelled, now }: MoneyContext
): BillingRow[] {
  if (!order || !collectsPayment(track)) return [];

  const rows: BillingRow[] = [{ label: 'Net price', value: netPrice(order) }, ...paymentRows(ledger)];
  rows.push(...refundRows(ledger, refunds));

  // Absent once cancelled: there is nothing left to collect, and the refund rows say what is owed
  // back. The age is the signal, not a colour.
  if (!cancelled) {
    const age = dayjs(now).diff(dayjs(order.createdAt), 'day');
    rows.push({
      label: 'Balance',
      value: order.balance > 0 ? `${formatDollars(order.balance)} · ${age} days` : formatDollars(0),
    });
  }

  return rows;
}
